import classnames from 'classnames';
import type { IntlKey } from 'locales';
import type { GimmickName } from 'models/forms/fields';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { GimmickItem } from './GimmickItem';

interface GimmickSelectorProps {
  selected: readonly GimmickName[];
  available: readonly GimmickName[];
  onGimmickSelectedChanged: (gimmickName: GimmickName, isSelected: boolean) => void;
  isDisabled: boolean;
  className?: string;
}
export const GimmickSelector: FC<GimmickSelectorProps> = ({
  available,
  selected,
  onGimmickSelectedChanged,
  isDisabled,
  className,
}) => {
  const myRootRef = useRef<HTMLDivElement>(null);

  const [isOpened, setIsOpened] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const dropdownId = '#dropdownMenuButton';

  const label = useMemo(() => formatMessage({ id: 'form.Gimmicks.label' }), [formatMessage]);

  const onDocumentClick = useCallback((evt: MouseEvent) => {
    if (myRootRef.current !== null && evt.target !== null) {
      if (myRootRef.current.contains(evt.target as Node) === false) {
        setIsOpened(false);
      }
    }
  }, []);

  useEffect(() => {
    if (isOpened) {
      document.addEventListener('click', onDocumentClick);
      return () => document.removeEventListener('click', onDocumentClick);
    }
  }, [isOpened, onDocumentClick]);

  const count = selected.length;

  return (
    <div className={classnames('d-flex btn-group', className)} role="group" ref={myRootRef}>
      <button
        className={classnames('btn btn-icon-start btn-icon-end', {
          'btn-flat-secondary': count === 0,
          'btn-flat-info': count > 0,
        })}
        type="button"
        id={dropdownId}
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded={isOpened}
        aria-label={label}
        disabled={isDisabled}
        onClick={() => setIsOpened(!isOpened)}
      >
        <i className="icon">widgets</i>
        <span className="d-none d-xxl-flex text-truncate">{label}</span>
        {count > 0 && <span className="badge rounded-pill badge-info ms-2">{count}</span>}
        <i className="icon">arrow_drop_down</i>
      </button>
      <div
        className={classnames('dropdown-menu top-100', { show: isOpened })}
        aria-labelledby={dropdownId}
        style={{ width: 'max-content' }}
      >
        <div className="mx-3">
          <ul className="list-group list-group-flush">
            {available.map(item => (
              <GimmickItem
                key={item}
                value={item}
                labelKey={`form.Gimmicks.option.${item}` as IntlKey}
                onCheckboxChanged={onGimmickSelectedChanged}
                isSelected={selected.includes(item)}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

GimmickSelector.displayName = 'GimmickSelector';
